/**
 * LytToast Component
 *
 * Individual toast notification component matching Figma design.
 * Supports success, error, warning, info, and normal types.
 *
 * @example
 * <LytToast
 *   id="toast-1"
 *   type="success"
 *   title="Saved"
 *   content="Your changes have been saved"
 *   timestamp={new Date()}
 *   onDismiss={() => handleDismiss('toast-1')}
 * />
 */

import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { TOAST_TYPES, TOAST_ANIMATIONS } from './toastConstants';
import LytButton, { BUTTON_STYLES } from '../library/button/LytButton';
import LytIcon, { ICON_NAMES } from '../library/icon/LytIcon';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';

// ============================================================================
// Toast Icon Mapping
// ============================================================================

const TOAST_ICON_MAP = {
    [TOAST_TYPES.SUCCESS]: ICON_NAMES.ToastSuccess,
    [TOAST_TYPES.ERROR]: ICON_NAMES.ToastError,
    [TOAST_TYPES.WARNING]: ICON_NAMES.ToastWarning,
    [TOAST_TYPES.INFO]: ICON_NAMES.ToastInfo,
    [TOAST_TYPES.NORMAL]: ICON_NAMES.ToastInfo, // Use info icon for normal as fallback
};

// ============================================================================
// Animations - Smooth spring-like motion
// ============================================================================

const slideIn = keyframes`
    0% {
        transform: translateX(-120%);
        opacity: 0;
    }
    100% {
        transform: translateX(0);
        opacity: 1;
    }
`;
const slideOut = keyframes`
    0% {
        transform: translateX(0);
        opacity: 1;
    }
    100% {
        transform: translateX(-120%);
        opacity: 0;
    }
`;

// ============================================================================
// Styled Components
// ============================================================================

const ToastContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 368px;
    padding: 16px;
    border-radius: 4px;
    box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.07),
        0px 8px 12px 0px rgba(136, 136, 136, 0.25);
    background-color: ${({ theme, $type }) =>
        theme.components.lyt_toast[$type]?.background};
    color: ${({ theme, $type }) => theme.components.lyt_toast[$type]?.text};
    animation: ${({ $isExiting }) => ($isExiting ? slideOut : slideIn)}
        ${({ $isExiting }) =>
            $isExiting
                ? TOAST_ANIMATIONS.EXIT_DURATION
                : TOAST_ANIMATIONS.ENTER_DURATION}ms
        cubic-bezier(0.4, 0, 0.2, 1) forwards;
    pointer-events: auto;

    ${({ theme, $type }) =>
        $type === TOAST_TYPES.NORMAL &&
        css`
            border: 1px solid ${theme.components.lyt_toast.normal.border};
        `}
`;
const ToastHeader = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    justify-content: center;
`;
const ToastIconWrapper = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;
const ToastContent = styled.div`
    flex: 1;
    min-width: 0;
    display flex;
    justify-content: center;
    align-items: center;
`;
const ToastTitle = styled.h4`
    margin: 0;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_BOLD)}
    letter-spacing: 0.14px;
    color: inherit;
`;
const ToastBody = styled.div`
    padding-left: 32px;
    padding-right: 32px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;
const ToastDescription = styled.p`
    margin: 0;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: inherit;
`;
const ToastTimestamp = styled.span`
    display: block;
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_NORMAL)}
    color: inherit;
`;
const ToastActions = styled.div`
    display: flex;
    gap: 4px;
    padding-top: 8px;
`;

// ============================================================================
// Component
// ============================================================================

const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date.toLocaleString();
};

/**
 * LytToast component
 */
const LytToast = ({
    id,
    type = TOAST_TYPES.NORMAL,
    title,
    content = '',
    timestamp = null,
    actions = [],
    autoClose = true,
    duration = 5000,
    onDismiss,
}) => {
    const [isExiting, setIsExiting] = useState(false);
    const styledTheme = useTheme();

    // Get the icon name for this toast type
    const toastIconName =
        TOAST_ICON_MAP[type] || TOAST_ICON_MAP[TOAST_TYPES.NORMAL];

    // Get the icon color from theme
    const iconColor = styledTheme.components.lyt_toast[type]?.icon;

    // Handle dismiss with exit animation
    const handleDismiss = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onDismiss(id);
        }, TOAST_ANIMATIONS.EXIT_DURATION);
    }, [id, onDismiss]);

    // Auto-close timer
    useEffect(() => {
        if (!autoClose || !duration) return;
        const timer = setTimeout(() => {
            handleDismiss();
        }, duration);
        return () => clearTimeout(timer);
    }, [autoClose, duration, handleDismiss]);

    // Handle action click
    const handleActionClick = useCallback(
        (action) => {
            if (action.onClick) {
                action.onClick();
            }
            // Optionally dismiss after action
            if (action.dismissOnClick !== false) {
                handleDismiss();
            }
        },
        [handleDismiss]
    );
    const hasBody = content || timestamp || (actions && actions.length > 0);
    return (
        <ToastContainer
            $type={type}
            $isExiting={isExiting}
            role="alert"
            aria-live="polite">
            <ToastHeader>
                <ToastIconWrapper>
                    <LytIcon name={toastIconName} color={iconColor} />
                </ToastIconWrapper>

                <ToastContent>
                    <ToastTitle>{title}</ToastTitle>
                </ToastContent>
                <LytButton
                    type={BUTTON_STYLES.toast_close}
                    icon={ICON_NAMES.X}
                    size="small"
                    customColor={styledTheme.components.lyt_toast[type]?.text}
                    onClick={handleDismiss}
                    aria-label="Dismiss notification"
                />
            </ToastHeader>

            {hasBody && (
                <ToastBody>
                    {content && <ToastDescription>{content}</ToastDescription>}
                    {timestamp && (
                        <ToastTimestamp>
                            {formatTimestamp(timestamp)}
                        </ToastTimestamp>
                    )}
                    {actions && actions.length > 0 && (
                        <ToastActions>
                            {actions.map((action, index) => (
                                <LytButton
                                    key={index}
                                    type={BUTTON_STYLES.toast}
                                    size="small"
                                    isCompact
                                    onClick={() => handleActionClick(action)}>
                                    {action.label}
                                </LytButton>
                            ))}
                        </ToastActions>
                    )}
                </ToastBody>
            )}
        </ToastContainer>
    );
};
LytToast.propTypes = {
    /** Unique toast identifier */
    id: PropTypes.string.isRequired,
    /** Toast type determines appearance */
    type: PropTypes.oneOf(Object.values(TOAST_TYPES)),
    /** Toast title (required) */
    title: PropTypes.string.isRequired,
    /** Toast content/description */
    content: PropTypes.string,
    /** Creation timestamp */
    timestamp: PropTypes.instanceOf(Date),
    /** Action buttons */
    actions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            onClick: PropTypes.func,
            dismissOnClick: PropTypes.bool,
        })
    ),
    /** Whether toast auto-closes */
    autoClose: PropTypes.bool,
    /** Duration in ms before auto-close */
    duration: PropTypes.number,
    /** Callback when toast is dismissed */
    onDismiss: PropTypes.func.isRequired,
};
export default LytToast;
